import {
  Component,
  h,
  Element,
  Prop,
  State,
  Event,
  EventEmitter,
  Watch,
  Host,
  Method,
  Listen,
} from "@stencil/core"
import {
  inputGroupReducer,
  getInitialState,
  InputGroupActions,
  InputGroupState,
} from "../input-group-store"
import {
  StoreConnection,
  StateHandle,
  parseFunction,
  closest,
  dispatchEvent,
  getAppearance,
  renderHiddenInput,
} from "../../../core/helpers/webcomponent-helpers"
import { IconName } from "../../../core/types/icon-names"

/**
 * @slot description - The supportive text.
 * @slot right - Text which is positioned at the right.
 * @slot infotip - The text displayed inside the infotip.
 */
@Component({
  tag: "sdx-input-item",
  styleUrl: "input-item.scss",
  shadow: true,
})
export class InputItem {
  private store?: StoreConnection<this, InputGroupState, InputGroupActions>
  private invokeChangeCallback: Function = () => null
  private selectedInputItemElsChangedInProgress = false
  private isReappending = false
  private labelEl?: HTMLLabelElement

  // A "native" hidden DOM element that is submitted when the item is used in a
  // traditional form.
  private lightDOMHiddenFormInputEl?: HTMLInputElement

  @Element() public el!: HTMLSdxInputItemElement

  @State() public state?: StateHandle<InputGroupState>
  @State() private wasIndeterminate = false

  @State() private focus = false
  @State() private focusVisible = false

  /**
   * The form input type of the item (radio or checkbox).
   */
  @Prop() public type: HTMLSdxInputGroupElement["type"] = "radio"

  /**
   * Icon name of the icon inside the container variation.
   */
  @Prop() public iconName?: IconName

  /**
   * Icon size of the icon inside the container variation.
   */
  @Prop() public iconSize: HTMLSdxIconElement["size"] = 2

  /**
   * Whether the item is turned on or off.
   */
  @Prop({ mutable: true }) public checked: boolean = false

  /**
   * Whether the item state is undetermined. Just used when `type="checkbox"`.
   */
  @Prop({ mutable: true }) public indeterminate: boolean = false

  /**
   * The current value.
   */
  @Prop() public value: any

  /**
   * Not selectable.
   */
  @Prop() public disabled: boolean = false

  /**
   * Callback whenever the user checks/unchecks the component.
   * @deprecated use input event instead.
   */
  @Prop() public changeCallback?: ((checked: boolean) => void) | string

  /**
   * Name parameter (useful when the component is used in a traditional HTML
   * form submit).
   */
  @Prop() public name?: string

  /**
   * Set this to false to declare the component as invalid and use the
   * `validation-message` attribute to explain why.
   */
  @Prop() public valid?: boolean

  /**
   * Text that explains the validation status to the user.
   */
  @Prop() public validationMessage?: string

  /**
   * Marks the component as required (please note that this itself does not
   * handle validation &mdash; use the "valid" and "validation-message" for
   * that).
   */
  @Prop() public required: boolean = false

  /**
   * What image to display (also supports SVG).
   */
  @Prop() public imageSrc?: string

  /**
   * An alternate text for the image if it can not be displayed and for screen readers.
   */
  @Prop() public imageAlt?: string

  /**
   * Hide the checkbox or radio checked indicator icon.
   * @private
   */
  @Prop() public hideCheckedIcon: boolean = false

  /**
   * @private
   */
  @Prop() public labelStyle: Record<string, string | undefined> = {}

  /**
   * @private
   */
  @Prop() public srHint?: string

  /**
   * This component supports common native HTML form input element events (such
   * as `input`, `focus`, `blur`, etc.).
   */
  @Event() public common!: EventEmitter

  @Watch("state")
  public stateChanged({}, prev: StateHandle<InputGroupState>) {
    const state = this.state!.get()
    const prevState = prev.get()

    if (state.selectedInputItemEls !== prevState.selectedInputItemEls) {
      this.selectedInputItemElsChanged(state)
    }
  }

  @Watch("checked")
  public checkedChanged() {
    if (!this.selectedInputItemElsChangedInProgress) {
      this.store?.dispatch({ type: "SELECT", inputItemEl: this.el })
    }
  }

  @Watch("changeCallback")
  public changeCallbackChanged() {
    this.setInvokeChangeCallback()
  }

  @Watch("indeterminate")
  public onIndeterminateChanged({}, prev: boolean) {
    if (prev) {
      this.wasIndeterminate = true

      this.labelEl?.addEventListener(
        "transitionend",
        () => {
          this.wasIndeterminate = false
        },
        { once: true },
      )
    }
  }

  @Method()
  public async doFocus() {
    this.lightDOMHiddenFormInputEl?.focus()
  }

  constructor() {
    // Check if it's a standalone input-item or part of an input-group (which uses a store)
    if (closest(this.el, "sdx-input-group")) {
      this.store = new StoreConnection(
        this,
        inputGroupReducer,
        getInitialState(),
        ["type", "theme", "name", "inline", "selectedInputItemEls"],
      )
    }

    this.setInvokeChangeCallback()
  }

  public connectedCallback() {
    if (this.isReappending) {
      this.isReappending = false
      return
    }

    // Register self
    this.store?.dispatch({
      type: "ADD_INPUT_ITEM_EL",
      inputItemEl: this.el,
    })

    if (this.checked) {
      this.store?.dispatch({ type: "SELECT", inputItemEl: this.el })
    }
  }

  public disconnectedCallback() {
    if (this.el.isConnected) {
      this.isReappending = true
      return
    }

    // Unregister self
    this.store?.dispatch({
      type: "REMOVE_INPUT_ITEM_EL",
      inputItemEl: this.el,
    })

    if (this.checked) {
      this.store?.dispatch({ type: "SELECT", inputItemEl: this.el })
    }
  }

  public componentWillLoad() {
    this.store?.subscribe()
  }

  private selectedInputItemElsChanged(state: InputGroupState) {
    this.selectedInputItemElsChangedInProgress = true

    this.checked = !!state.selectedInputItemEls.includes(this.el)

    this.selectedInputItemElsChangedInProgress = false
  }

  private getInputType(): this["type"] {
    return this.state?.get().type || this.type
  }

  /**
   * This handler is also being triggered by hitting the spacebar.
   * The change callback is also called here (instead of in the @Watch("checked")),
   * because native HTML checkboxes and radios don’t call their change callback
   * when the .checked property is changed programmatically.
   */
  private onInputElClick(): void {
    if (this.getInputType() === "radio") {
      // "radio"
      if (!this.checked) {
        // only check radio when it's not checked yet
        this.checked = true
        this.invokeChangeCallback(this.checked)
      }
    } else {
      // "checkbox"
      this.indeterminate = false
      this.checked = !this.checked
      this.invokeChangeCallback(this.checked)
    }
  }

  private updateHiddenFormInputEl(): void {
    const {
      state,
      el,
      name,
      value,
      disabled,
      checked,
      srHint,
      validationMessage,
      indeterminate,
      required,
    } = this

    const inputName = state?.get().name || name || ""
    const inputValue = checked ? value || "on" : value || ""

    renderHiddenInput(el, inputName, inputValue, disabled)

    this.lightDOMHiddenFormInputEl = el.querySelector(
      "input.aux-input",
    ) as HTMLInputElement

    if (this.lightDOMHiddenFormInputEl) {
      this.lightDOMHiddenFormInputEl.onfocus = () => {
        this.focus = true
        this.focusVisible =
          this.lightDOMHiddenFormInputEl!.matches(":focus-visible")
      }

      this.lightDOMHiddenFormInputEl.onblur = () => {
        this.focus = false
        this.focusVisible = false
      }

      this.lightDOMHiddenFormInputEl.onchange = () => {
        this.checked = this.lightDOMHiddenFormInputEl!.checked
      }

      // onClick because Angular doesn't detect onChange
      // (please note that onClick is also fired when hitting the spacebar)
      this.lightDOMHiddenFormInputEl.onclick = () => this.onInputElClick()

      this.lightDOMHiddenFormInputEl.oninput = (e) => {
        e.stopPropagation()
        dispatchEvent(el, "input")
      }

      const childrenTextContent: (string | null)[] = []

      el.childNodes.forEach((node) =>
        childrenTextContent.push(node.textContent),
      )

      // "left" for VoiceOver outline
      this.lightDOMHiddenFormInputEl.setAttribute(
        "style",
        "position: absolute; opacity: 0; cursor: inherit; left: 0;",
      )

      // adding commas so that screen reader can pronounce better
      const ariaLabelValue = [...childrenTextContent, srHint, validationMessage]
        .filter((item) => item)
        .join(",")

      this.lightDOMHiddenFormInputEl.ariaLabel = ariaLabelValue
      this.lightDOMHiddenFormInputEl.type = this.getInputType()
      // Set indeterminate property for screen reader
      this.lightDOMHiddenFormInputEl.indeterminate = indeterminate
      this.lightDOMHiddenFormInputEl.checked = checked
      this.lightDOMHiddenFormInputEl.tabIndex = this.hasCallToAction() ? -1 : 0
      this.lightDOMHiddenFormInputEl.required = required
    }
  }

  private setInvokeChangeCallback(): void {
    this.invokeChangeCallback = parseFunction(this.changeCallback)
  }

  private getHostClassNames() {
    return {
      "input-container": this.state?.get().theme === "container",
      [getAppearance(this.el)]: true,
    }
  }

  private getComponentClassNames() {
    return {
      component: true,
      [getAppearance(this.el)]: true,
      inline: !!this.state?.get().inline,
      input: true,
      [this.getInputType()]: true,
      disabled: this.disabled,
      "indicator-less": this.isIndicatorLess(),
      "has-cta": this.hasCallToAction(),
    }
  }

  // { capture: true } to make sure that stopPropagation() works
  @Listen("click", { capture: true })
  public onClick(e: Event) {
    if (e.target === this.el) {
      e.preventDefault()
      e.stopPropagation()

      if (this.hasCallToAction()) {
        return
      }

      this.lightDOMHiddenFormInputEl?.click()
      this.lightDOMHiddenFormInputEl?.focus()
      this.focusVisible = false // workaround for flaky match(":focus-visible")
    }
  }

  private isIndicatorLess(): boolean {
    return this.state?.get().theme === "indicator-less"
  }

  private hasCallToAction(): boolean {
    return !!this.el.querySelector("sdx-button") && this.isIndicatorLess()
  }

  public render() {
    this.updateHiddenFormInputEl()

    return (
      <Host class={this.getHostClassNames()}>
        <div class={this.getComponentClassNames()}>
          <input
            id="input"
            type={this.getInputType()}
            checked={this.checked}
            disabled={this.disabled}
            indeterminate={this.indeterminate}
            tabindex={-1}
            class={{
              focus: this.focus,
              "focus-visible": this.focusVisible,
              invalid: this.valid === false,
              "hide-checked-icon":
                this.hideCheckedIcon || this.isIndicatorLess(),
              indeterminate: this.indeterminate,
              "was-indeterminate": this.wasIndeterminate,
            }}
          />

          <label
            htmlFor="input"
            style={this.labelStyle}
            class={{
              // lightDOMHiddenFormInputEl is always a child
              "has-text": this.el.childNodes.length > 1,
            }}
            ref={(el) => (this.labelEl = el)}
          >
            <div class="top-section">
              <div class="top-section-content">
                <slot />

                {this.required && <span aria-hidden="true">&nbsp;*</span>}

                <span class="description">
                  <slot name="description" />
                </span>
              </div>

              <span class="right">
                <slot name="right" />
              </span>
            </div>

            {this.imageSrc && (
              <img class="image" src={this.imageSrc} alt={this.imageAlt} />
            )}

            {/* Only input-items in containers are allowed to have icons */}
            {this.state?.get().theme === "container" && this.iconName && (
              <div aria-hidden="true">
                {/* The first icon is just a space reservation because the second one is absolute */}
                <sdx-icon
                  class="icon-placeholder"
                  icon-name={this.iconName}
                  size={this.iconSize}
                />

                <sdx-icon
                  class="icon"
                  icon-name={this.iconName}
                  size={this.iconSize}
                />
              </div>
            )}
          </label>

          <div aria-live="assertive">
            {this.validationMessage && (
              <sdx-validation-message
                validationMessage={this.validationMessage}
              />
            )}
          </div>
        </div>
      </Host>
    )
  }
}
